/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import NumberFormat, { NumberFormatValues } from 'react-number-format'
import { Badge, Modal, Tabs } from 'antd-mobile'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import * as Utils from '@utils/utils'
import {
  formatNumber,
  formatNumberTicketCount,
  getPropertyIdByBetSlipIndexKey,
  getRatioTicketMoney,
  getTextByPropertyId,
  getTicketCountFromTicket,
  getTicketFromTicketCount,
  makeLimitBettingRateText,
  makeMaxBetAlertText,
  showToast,
  TOAST_TYPE,
} from '@utils/utils'
import { observer } from 'mobx-react'
import Dock from 'react-dock'
import { PROPERTY_ID } from 'adv-protocol/src/GameCommon'
import { BET_SLIP_BTN_TYPE, BET_SLIP_TAB_INDEX_KEY, BET_TICKET_TYPE } from '@constants/index'
import BubblePopup from '@views/Matchup/BetSlip/Bubble'
import { exchangeMoneyByBetMoneyType, isExceedMaxBetMoney, makeFoldItems } from '@views/Matchup/BetSlip/BetSlipHelper'
import { BubbleTextType, BubbleType } from '@store/globalStore'
import BettingHistory from '@views/BettingHistory'
import BottomLoading from '@components/BottomLoading'
import { Models } from 'rmc-tabs/lib/Models'
import * as MatchUtils from '@v2/utils/match-utils'
import * as _ from 'lodash'
// icon
import { DiaIco, GmoneyIco } from '@shared/Icon/MoneyIcon'
// css
import { BadgeStyle, ModalBg, TabTxt, WhiteChildTabWrap } from '@styles/base.style'
import { Button, CloseBtn } from '@styles/button.style'
import { BetSlipBottomModal, BetSlipBottomWrapper, BetSlipListWrap } from '@views/Matchup/BetSlip/index.style'
import BetCouponImg from '@assets/images/icon/matchup_icon/betting-coupon-10000.png'
import { DiaTxtIco } from '@shared/Icon/EtcIcon'
import classNames from 'classnames'
import { FROM_VIEW_TYPE } from '@v2/constants/common'

const ComponentBetSlip: React.FC = props => {
  const [currentTabIndexKey, setCurrentTabIndexKey] = useState(BET_SLIP_TAB_INDEX_KEY.MONEY)
  const [limitBettingRateText, setLimitBettingRateText] = useState('100')

  // 동일한 값이 여러번 셋팅되는 경우에도 화면을 다시 렌더링 할 수 있도록 오브젝트로 선언
  // ex) 사용자가 베팅 범위를 넘게 베팅 금액을 입력하는 경우 setCurrentBetMoney 값이 같아
  //     화면 렌더링이 이루어지지 않고 사용자 입력값이 유지되는 현상
  const [currentBetMoney, setCurrentBetMoney] = useState({ amount: null })

  const { globalStore, componentStore, authStore, bettingHistoryStore, logStore } = useRootStore()
  const { userInfo } = authStore
  const { money, ticket, dia, diaCasino } = userInfo
  const { onGoingBetCount } = bettingHistoryStore
  const { componentBettingParams = { matches: [], club: null }, isBetting, cartItems } = componentStore
  const { matches, club } = componentBettingParams
  const clubId = club?.id
  const diaMoney = Number(dia) - Number(diaCasino)

  const { visibleComponentBetSlip, visibleComponentBetCompleteAni } = globalStore
  const currentBetMoneyType = getPropertyIdByBetSlipIndexKey(currentTabIndexKey)

  const betInputClassName = 'bet-input-aaaaa'

  useEffect(() => {
    if (!_.isEmpty(clubId)) {
      initializeClubOptions()
    }
  }, [clubId])

  const initializeClubOptions = async () => {
    await componentStore.refreshClubOption(clubId, getPropertyIdByBetSlipIndexKey(currentTabIndexKey))
    const { clubOption } = componentStore
    setLimitBettingRateText(makeLimitBettingRateText(_.toNumber(clubOption.limit_betting_rate)))
  }

  const getTargetUserMoney = () => {
    let amount: number
    switch (currentTabIndexKey) {
      case BET_SLIP_TAB_INDEX_KEY.MONEY:
        amount = money
        break
      case BET_SLIP_TAB_INDEX_KEY.BET_TICKET:
        amount = ticket
        break
      case BET_SLIP_TAB_INDEX_KEY.DIA:
        amount = diaMoney
        break
    }
    return amount
  }

  const validateBetMoney = (betMoney: number | string, isInputEvent = false): number => {
    if (_.isEmpty(_.trim(betMoney as string))) {
      return null
    }

    let validMoney = _.toNumber(betMoney)

    const propertyId = getPropertyIdByBetSlipIndexKey(currentTabIndexKey)
    const maxBetMoney = maxBetMoneyByBetMoneyType(propertyId)
    const targetUserMoney = getTargetUserMoney()

    if (validMoney > targetUserMoney || targetUserMoney === 0) {
      // 금액 입력 버튼으로 입력시 현재 값 유지, 입력해서 업데이트 할때는 최대값 설정
      if (!isInputEvent) {
        showToast(TOAST_TYPE.INFO, `보유한 ${getTextByPropertyId(currentBetMoneyType)}가 부족합니다.`)
      }
      validMoney = isInputEvent ? targetUserMoney : currentBetMoney.amount
    }

    if (isExceedMaxBetMoney(validMoney, maxBetMoney)) {
      // 수동입력과 버튼으로 자동 입력 구분하여 체크
      if (isInputEvent || currentBetMoney.amount >= maxBetMoney) {
        const maxBetAlertText = makeMaxBetAlertText(currentBetMoneyType, maxBetMoney)
        globalStore.showBubblePopupTimer(BubbleTextType.MAX_BET_ALERT, maxBetAlertText, BubbleType.COMMON_BET_SLIP)
      }
      validMoney = maxBetMoney
    }

    return validMoney
  }

  const validateBetMoneyByTicket = (count: number, type: BET_TICKET_TYPE): number | null => {
    const betTicket = getTicketFromTicketCount(type, count)
    if (count !== Number.MAX_SAFE_INTEGER && (targetBetMoney + betTicket) > userInfo.ticket) {
      showToast(TOAST_TYPE.INFO, '보유 베팅권이 부족합니다.')
      return null
    }

    const propertyId = getPropertyIdByBetSlipIndexKey(currentTabIndexKey)
    const maxBetMoney = maxBetMoneyByBetMoneyType(propertyId)

    let changeableMoney

    if (isExceedMaxBetMoney(targetBetMoney + betTicket, maxBetMoney)) {
      if (targetBetMoney >= maxBetMoney) {
        const maxBetAlertText = makeMaxBetAlertText(currentBetMoneyType, maxBetMoney * 0.1)
        globalStore.showBubblePopupTimer(BubbleTextType.MAX_BET_ALERT, maxBetAlertText, BubbleType.COMMON_BET_SLIP)
      }
      changeableMoney = maxBetMoney
    } else if (count + _.toNumber(currentBetMoney.amount) < 0) {
      changeableMoney = 0
    } else {
      changeableMoney = targetBetMoney + betTicket
    }

    return changeableMoney
  }

  const validateBetMoneyByTicketAllin = (): number => {
    let changeableMoney = 0

    const propertyId = getPropertyIdByBetSlipIndexKey(currentTabIndexKey)
    const maxBetMoney = maxBetMoneyByBetMoneyType(propertyId)
    if (isExceedMaxBetMoney(ticket, maxBetMoney)) {
      if (targetBetMoney >= maxBetMoney) {
        const maxBetAlertText = makeMaxBetAlertText(currentBetMoneyType, maxBetMoney * 0.1)
        globalStore.showBubblePopupTimer(BubbleTextType.MAX_BET_ALERT, maxBetAlertText, BubbleType.COMMON_BET_SLIP)
      }
      changeableMoney = maxBetMoney
    } else {
      const ticketCount = getTicketCountFromTicket(BET_TICKET_TYPE.BET_TICKET1, ticket)
      changeableMoney = getTicketFromTicketCount(BET_TICKET_TYPE.BET_TICKET1, ticketCount)
    }

    return changeableMoney
  }

  const updateBetMoney = (betMoney: number | string, isInputEvent = false) => {
    const validMoney = validateBetMoney(betMoney, isInputEvent)
    setCurrentBetMoney({ amount: validMoney })
  }

  const updateBetMoneyByTicket = (count: number, type: BET_TICKET_TYPE) => {
    const validMoney = validateBetMoneyByTicket(count, type)
    if (!_.isNumber(validMoney)) {
      return
    }
    setCurrentBetMoney({ amount: validMoney })
  }

  const updateBetMoneyTicketAllin = () => {
    const validMoney = validateBetMoneyByTicketAllin()
    setCurrentBetMoney({ amount: validMoney })
  }

  const onPriceValueChange = (values: NumberFormatValues) => {
    const { value } = values
    updateBetMoney(value, true)
  }

  const onChangeTab = async (tab: Models.TabData) => {
    setCurrentTabIndexKey(tab.key as BET_SLIP_TAB_INDEX_KEY)
    onClickMoneyReset()
    const propertyId = getPropertyIdByBetSlipIndexKey(tab.key as BET_SLIP_TAB_INDEX_KEY)
    await componentStore.refreshClubOption(clubId, propertyId)
    const { clubOption } = componentStore
    setLimitBettingRateText(makeLimitBettingRateText(_.toNumber(clubOption.limit_betting_rate)))
  }

  const doBet = () => {
    componentStore.setComponentBettingParams({
      ...componentStore.componentBettingParams, betMoney: currentBetMoney.amount, betMoneyType: currentBetMoneyType
    }, true)
    componentStore.bet()
  }


  const maxBetMoneyByBetMoneyType = (id: PROPERTY_ID) => {
    let maxBetMoney
    // 공유받은 베팅내역으로 베팅시에는 공유한 유저가 베팅한 클럽의 옵션을 따라야 하기 때문에
    // matchupStore 의 clubOption 을 사용하지 않는다.
    const { clubOption } = componentStore

    switch (id) {
      case PROPERTY_ID.MONEY:
        maxBetMoney = globalStore.getClubGmoneyMaxBetLimit()
        break
      case PROPERTY_ID.NEW_BET_TICKET_G:
        maxBetMoney = globalStore.getClubGmoneyMaxBetLimit() * 10
        break
      case PROPERTY_ID.DIA:
        maxBetMoney = clubOption.pre_betmoney_max
        break
    }

    return maxBetMoney
  }

  const onClickMoneyButton = (value: number) => {
    updateBetMoney(currentBetMoney.amount + _.toNumber(value))
  }

  const onClickMoneyAllin = () => {
    updateBetMoney(getTargetUserMoney())
  }

  const onClickTicketAllin = () => {
    updateBetMoneyTicketAllin()
  }

  const onClickMoneyReset = () => {
    updateBetMoney(null)
  }

  const onClickMessagePopupCloseButton = () => {
    componentStore.clearCartItems()
    globalStore.setVisibleComponentBetSlip(false)
  }

  const onVisibleChange = e => {
    globalStore.setVisibleComponentBetSlip(false)
  }

  const visible = visibleComponentBetSlip
  if (!visible) {
    return <Fragment/>
  }

  let dividendRateFromFold = 1.0

  for (const match of matches) {
    for (const fold of match.folds) {
      dividendRateFromFold *= fold.outcome.rate
    }
  }

  dividendRateFromFold = MatchUtils.rateFloorCalculate(dividendRateFromFold)

  let foldCount = 0
  matches.forEach(match => {
    foldCount += match?.folds.length ?? 0
  })

  const foldNum = foldCount
  const calDividendRate = dividendRateFromFold
  const dividendRate = MatchUtils.rateFloorCalculate(calDividendRate)

  const dividendMoney = MatchUtils.rateFloorCalculateWithExtraValue(dividendRate, _.toNumber(currentBetMoney.amount))
  const formattedDividendMoney = formatNumber(
    exchangeMoneyByBetMoneyType(Math.floor(dividendMoney), currentBetMoneyType)
  )

  const dockZindex = 102
  const dockSize = 36

  const className = []
  className.push('short-top pc-close-btn-modal')
  className.push('message-popup')
  className.push('pick-bet-slip-popup')

  const tabs = []
  tabs.push({
    title: <TabTxt className="white-tab-txt">G머니</TabTxt>,
    key: BET_SLIP_TAB_INDEX_KEY.MONEY,
  })

  if (globalStore.getUseDia()) {
    tabs.push({
      title: <TabTxt className="white-tab-txt dia-tab-txt">
        <DiaIco/><DiaTxtIco/>
      </TabTxt>,
      key: BET_SLIP_TAB_INDEX_KEY.DIA,
    })
  }

  // 베팅권 수량이 유효한 경우만 베팅권 탭을 표시한다.
  if (userInfo.ticket && getTicketCountFromTicket(BET_TICKET_TYPE.BET_TICKET1, userInfo.ticket) > 0) {
    tabs.push({
      title: <TabTxt className="white-tab-txt">베팅권</TabTxt>,
      key: BET_SLIP_TAB_INDEX_KEY.BET_TICKET,
    })
  }

  const gMoneyButtonMobile = [
    BET_SLIP_BTN_TYPE.RESET,
    BET_SLIP_BTN_TYPE.MAX,
    BET_SLIP_BTN_TYPE.TEN_THOUSAND,
    BET_SLIP_BTN_TYPE.FIFTY_THOUSAND,
    BET_SLIP_BTN_TYPE.ONE_HUNDRED_THOUSAND,
    BET_SLIP_BTN_TYPE.FIVE_HUNDRED_THOUSAND]

  const diaButtonMobile = [
    BET_SLIP_BTN_TYPE.RESET,
    BET_SLIP_BTN_TYPE.MAX,
    BET_SLIP_BTN_TYPE.TEN_THOUSAND,
    BET_SLIP_BTN_TYPE.FIFTY_THOUSAND,
    BET_SLIP_BTN_TYPE.ONE_HUNDRED_THOUSAND,
    BET_SLIP_BTN_TYPE.ONE_MILLION]

  const couponButtonMobile = [
    BET_SLIP_BTN_TYPE.RESET,
    BET_SLIP_BTN_TYPE.MAX,
    BET_SLIP_BTN_TYPE.ONE,
    BET_SLIP_BTN_TYPE.FIVE,
    BET_SLIP_BTN_TYPE.TEN,
    BET_SLIP_BTN_TYPE.FIFTY]

  const targetBetMoney = _.toNumber(currentBetMoney.amount)

  const tabRenderContent = (tab) => {
    const tabKey = tab.key
    const betSlipId = `component_betSlip_${tabKey}`

    // const funcSetBetMoney = componentStore.setBetMoney
    const totalBetMoney = getRatioTicketMoney(targetBetMoney)

    const onClickTicketCountButtonHandler = (count: number, type: BET_TICKET_TYPE) => {
      updateBetMoneyByTicket(count, type)
    }

    const onClickBetSlipList = () => {
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.COMPONENT_BET_SLIP)
      bettingHistoryStore.setBettingHistoryInitProps({
        onlyMajorClub: false,
        fromViewType: FROM_VIEW_TYPE.COMPONENT_BET_SLIP,
      })
      if (isMobile) {
        globalStore.setVisibleBubbleBetList(true)
      } else {
        onClickMessagePopupCloseButton()
        globalStore.addGeneralPopupProperties(<BettingHistory/>, '베팅 내역', {
          wrapClassName: '', className: '', isScroll: false, custom: false, clsBtn: false,
          closeCallback: () => {
            logStore.writeButtonLog({ pageId: 'c010', buttonId: '002' }).catch(console.error)
          },
        })
      }
    }

    const makeTicketButton = (value: number) => {
      const key = `coupon_bet_btn_${value}`
      return (
        <Button
          key={key}
          secondary
          className="bet-btn bet-coupon"
          onClick={() => onClickTicketCountButtonHandler(value, BET_TICKET_TYPE.BET_TICKET1)}
        >
          <img
            src={BetCouponImg}
            alt="베팅권"
          />
          <span>
            <span className="coupon-value"/>{value}
          </span>
        </Button>
      )
    }

    const makeMoneyButton = (value: number) => {
      const key = `money_bet_btn_${value}`
      return (
        <Button
          key={key}
          secondary
          className="bet-btn"
          onClick={() => onClickMoneyButton(value)}
        >
          {formatNumber(value)}<GmoneyIco/>
        </Button>
      )
    }

    const makeDiaButton = (value: number) => {
      const key = `money_bet_btn_${value}`
      return (
        <Button
          key={key}
          secondary
          className="bet-btn"
          onClick={() => onClickMoneyButton(value)}
        >
          {formatNumber(value)}<DiaIco/>
        </Button>
      )
    }

    const makeResetButton = (value) => {
      const key = `reset_bet_btn_${value}`
      return (
        <Button
          key={key}
          className="bet-btn other"
          onClick={() => onClickMoneyReset()}
        >
          {value}
        </Button>
      )
    }

    const makeMaxButton = (value) => {
      const key = `max_bet_btn_${value}`
      return (
        <Button
          key={key}
          className="bet-btn other"
          onClick={() => onClickMoneyAllin()}
        >
          {value}
        </Button>)
    }

    const makeMaxCouponButton = (value) => {
      const key = `max_bet_btn_${value}`
      return (
        <Button
          key={key}
          className="bet-btn other"
          onClick={() => onClickTicketAllin()}
        >
          {value}
        </Button>)
    }

    const makeGMoneyBetButton = () => {
      const makeButtons = []
      const targetBtns = gMoneyButtonMobile

      for (let i = 0; i < targetBtns.length; i += 1) {
        if (targetBtns[i] === BET_SLIP_BTN_TYPE.MAX) {
          makeButtons.push(makeMaxButton(targetBtns[i]))
        } else if (targetBtns[i] === BET_SLIP_BTN_TYPE.RESET) {
          makeButtons.push(makeResetButton(targetBtns[i]))
        } else {
          makeButtons.push(makeMoneyButton(Number(targetBtns[i])))
        }
      }

      return makeButtons
    }

    const makeDiaBetButton = () => {
      const makeButtons = []
      const targetBtns = diaButtonMobile

      for (let i = 0; i < targetBtns.length; i += 1) {
        if (targetBtns[i] === BET_SLIP_BTN_TYPE.MAX) {
          makeButtons.push(makeMaxButton(targetBtns[i]))
        } else if (targetBtns[i] === BET_SLIP_BTN_TYPE.RESET) {
          makeButtons.push(makeResetButton(targetBtns[i]))
        } else {
          makeButtons.push(makeDiaButton(Number(targetBtns[i])))
        }
      }

      return makeButtons
    }

    const makeTicketBetButton = () => {
      const makeButtons = []
      const targetBtns = couponButtonMobile

      for (let i = 0; i < targetBtns.length; i += 1) {
        if (targetBtns[i] === BET_SLIP_BTN_TYPE.MAX) {
          makeButtons.push(makeMaxCouponButton(targetBtns[i]))
        } else if (targetBtns[i] === BET_SLIP_BTN_TYPE.RESET) {
          makeButtons.push(makeResetButton(targetBtns[i]))
        } else {
          makeButtons.push(makeTicketButton(Number(targetBtns[i])))
        }
      }

      return makeButtons
    }

    // 모바일과 PC 버튼 순서가 달라서 구분해두었습니다.
    const gMoneyButton = (
      <Fragment>
        <span className="bet-input">
          <NumberFormat
            className={betInputClassName}
            pattern="\d*"
            decimalScale={0}
            maxLength={20}
            allowNegative={false}
            value={currentBetMoney.amount || ''}
            thousandSeparator
            onValueChange={onPriceValueChange}
            // disabled={isInputNotAvailable(targetCart) || (betMoneyType === PROPERTY_ID.MONEY && money <= 0)}
          />
          <GmoneyIco/>
        </span>
        {makeGMoneyBetButton()}
      </Fragment>
    )

    const diaButton = (
      <Fragment>
        <span className="bet-input">
          <NumberFormat
            className={betInputClassName}
            pattern="\d*"
            decimalScale={0}
            maxLength={20}
            allowNegative={false}
            value={currentBetMoney.amount || ''}
            thousandSeparator
            onValueChange={onPriceValueChange}
          />
          <DiaIco/>
        </span>
        {makeDiaBetButton()}
      </Fragment>
    )

    const betCoupon = (
      <Fragment>
        <span className="bet-input">
          {formatNumber(totalBetMoney)}
          <GmoneyIco/>
        </span>
        {makeTicketBetButton()}
      </Fragment>
    )

    const foldItems = makeFoldItems(cartItems, null)

    const gMoneyInfo = (
      <Fragment>
        <span className="detail-txt">보유머니</span>
        <span className="detail-num">{formatNumber(money)}<GmoneyIco/></span>
      </Fragment>
    )
    const diaInfo = (
      <Fragment>
        <span className="detail-txt">보유다이아</span>
        <span className="detail-num">{formatNumber(diaMoney)}<DiaIco/></span>
      </Fragment>
    )
    const couponInfo = (
      <Fragment>
        <span className="detail-txt">보유베팅권</span>
        <span className="detail-num">{formatNumberTicketCount(BET_TICKET_TYPE.BET_TICKET1, ticket)}
          <span className="txt">개</span>
        </span>
      </Fragment>
    )

    let betSlipInfo
    let betSlipInputArea

    if (currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.MONEY) {
      betSlipInfo = gMoneyInfo
      betSlipInputArea = gMoneyButton
    } else if (currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.DIA) {
      betSlipInfo = diaInfo
      betSlipInputArea = diaButton
    } else {
      betSlipInfo = couponInfo
      betSlipInputArea = betCoupon
    }

    // 급액 버튼 영역
    // const betSlipInputArea = selectedTabIndexKey === BET_SLIP_TAB_INDEX_KEY.MONEY ? gMoneyButton : betCoupon

    return (
      <Fragment>
        <ul id={betSlipId} className={'bet-list'}>
          {foldItems}
        </ul>
        <div className="bet-info">
          <ul className="bet-detail-info">
            <li>
              <span className="detail-txt">최대배당</span>
              <span className="detail-num max-value">
                {limitBettingRateText}
                <span className="txt">배</span>
              </span>
            </li>
            <li>
              <span className="detail-txt">총배당</span>
              <span className="detail-num point total">{dividendRate.toFixed(2)}</span>
            </li>
            <li>
              {betSlipInfo}
            </li>
            <li>
              <span className="detail-txt">
                {currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.DIA && '예상적중다이아'}
                {(currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.MONEY || currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.BET_TICKET) && '예상적중머니'}
              </span>
              <span className="detail-num point">
                {formattedDividendMoney}
                {currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.DIA && <DiaIco/>}
                {(currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.MONEY
                  || currentTabIndexKey === BET_SLIP_TAB_INDEX_KEY.BET_TICKET) && <GmoneyIco/>}</span>
            </li>
          </ul>

          {/* TODO 탭이 변경될 시 입력된 재화 값은 초기화되어야 합니다. */}
          {betSlipInputArea}

          <Button
            className='ongoing-bet-btn component-bet-slip'
            onClick={onClickBetSlipList}
          >
            {isMobile ? '진행중 베팅' : '진행중'}
            <Badge
              text={onGoingBetCount}
              overflowCount={99}
              css={BadgeStyle}
              className={visibleComponentBetCompleteAni && 'scale-ani'}
            />
            {visibleComponentBetCompleteAni && <span className="plus-ani">+1</span>}
          </Button>

          <Button
            onClick={(e) => {
              e.currentTarget.blur()
              doBet()
            }}
            className='do-bet-btn component-bet-slip'
            secondary>
            {/* ***** 베팅 완료되기까지의 로딩 ***** */}
            {isBetting ? <BottomLoading/> : '베팅하기'}
          </Button>
        </div>
      </Fragment>
    )
  }

  const betSlipListWrapClass = classNames('', {
    'android-betslip': isAndroid,
    'ios-betslip': isIOS,
  })

  const renderContent = () => {
    const contents = (
      <BetSlipBottomWrapper className={isMobile ? 'wrapping component-bet-slip' : 'wrapping component-bet-slip pc'}>

        <div className="betslip-detail">
          <WhiteChildTabWrap className="betslip-tab">
            {club?.listTextImageUrl && Utils.isMembershipClub(club?.grade) && <div className="club-list-wrap">
              <img src={club?.listTextImageUrl} onError={null} alt="클럽 아이콘" loading="lazy" className="icon"/>
            </div>
            }
            <Button className="toggle-betslip-btn" onClick={onClickMessagePopupCloseButton}>
              <span className="cls-ico"/>
            </Button>
            <BetSlipListWrap className={betSlipListWrapClass}>
              <div className="bet-header">
                <span className="fold no-line">
                  <span className="fold-num">{foldNum}</span>
                  폴드
                </span>
              </div>

              <Tabs
                tabs={tabs}
                initialPage={0}
                page={currentTabIndexKey}
                onChange={onChangeTab}
                animated={false}
                swipeable={false}
                renderTabBar={tabProps => {
                  return <Tabs.DefaultTabBar {...tabProps} page={tabs.length}/>
                }}
              >
                {tabRenderContent}
              </Tabs>
            </BetSlipListWrap>

          </WhiteChildTabWrap>
          <BubblePopup type={BubbleType.COMMON_BET_SLIP}/>
        </div>
      </BetSlipBottomWrapper>
    )

    if (isMobile) {
      return (
        <Fragment>
          {contents}
        </Fragment>
      )
    }
    return contents
  }
  return (
    <Fragment>
      {isMobile ? (
        <Dock
          zIndex={dockZindex}
          position="bottom"
          fluid={false}
          size={dockSize}
          dimMode="opaque"
          isVisible={visible}
          onVisibleChange={onVisibleChange}
          dockStyle={{
            bottom: 0,
            width: '100%',
            minHeight: '1px',
            maxHeight: '400px',
            padding: '0',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -10px 4px rgba(0, 0, 0, 0.15)',
          }}
        >
          <BetSlipBottomModal>
            {renderContent()}
          </BetSlipBottomModal>

          <ModalBg onClick={onClickMessagePopupCloseButton}/>
        </Dock>
      ) : (
        <Modal
          popup
          visible={visible}
          maskClosable={false}
          animated={isMobile}
          animationType="slide-up"
          onClose={onClickMessagePopupCloseButton}
          className={className.join(' ')}
        >
          <Button
            circle onClick={onClickMessagePopupCloseButton}
            css={CloseBtn}
            className="close-btn modal-circle-close-btn">
            <span className="close-txt">닫기</span>
          </Button>
          <BetSlipBottomWrapper className="pc">
            {renderContent()}
          </BetSlipBottomWrapper>
          <ModalBg onClick={onClickMessagePopupCloseButton}/>
        </Modal>
      )}
    </Fragment>
  )
}

export default observer(ComponentBetSlip)
